import type { WithContext, Thing } from "schema-dts";
import type { MetaDescriptor } from "@remix-run/node";
import {
  SeoConfig as _SeoConfig,
  getSeoMeta as _getSeoMeta,
} from "@shopify/hydrogen";

import type { MetadataType } from "@bared/groq";

import type { ServerContext } from "./server";

const DEFAULT_SHARE_IMAGE =
  "https://cdn.shopify.com/s/files/1/0322/6841/9208/files/250306-URL-Share-v2.jpg?v=1741912416";

export type SeoConfig = _SeoConfig & {
  facebookAppId?: string;
  googleSiteVerification?: string;
};

export type SeoSchema = WithContext<Thing>;

/**
 * Generate a remix meta array from the given SeoConfig objects.
 * This extends the Hydrogen utility with some of our own meta tags.
 * @see https://shopify.dev/docs/api/hydrogen/2024-04/utilities/getseometa
 */
export function getSeoMeta(...seoInputs: SeoConfig[]): MetaDescriptor[] {
  const meta = _getSeoMeta(...seoInputs) ?? [];

  for (const seoInput of seoInputs) {
    if (seoInput.facebookAppId)
      meta.push({ property: "fb:app_id", content: seoInput.facebookAppId });
    if (seoInput.googleSiteVerification)
      meta.push({
        property: "google-site-verification",
        content: seoInput.googleSiteVerification,
      });
  }

  return meta;
}

/**
 * Return SeoConfig for the given page.
 * @see https://shopify.dev/docs/api/hydrogen/2024-04/utilities/getseometa
 */
export function seoConfigForPage({
  schemas = [],
  media: _media,
  title: _title,
  description: _description,
  sanityMetadata,
  context: { locale, urlContext, sanityImageUrlBuilder },
}: {
  title?: string | null;
  description?: string | null;
  media?: SeoConfig["media"] | null;
  schemas?: SeoSchema[];
  sanityMetadata?: MetadataType | null;
  context: Pick<
    ServerContext,
    "locale" | "urlContext" | "sanityImageUrlBuilder"
  >;
}): SeoConfig {
  // Calculate a page title and description.
  const title = _title ?? sanityMetadata?.title;
  const description = _description ?? sanityMetadata?.description;

  // Calculate robots.
  const robots: SeoConfig["robots"] = {
    noIndex:
      "boolean" === typeof sanityMetadata?.noIndex
        ? sanityMetadata.noIndex
        : false,
  };

  // Calculate an image.
  const media: SeoConfig["media"] =
    _media ??
    (sanityMetadata?.image
      ? sanityImageUrlBuilder.image(sanityMetadata.image).width(1200).url()
      : DEFAULT_SHARE_IMAGE);

  // Calculate alternate locales.
  const alternates: SeoConfig["alternates"] = [
    ...urlContext.localizedUrls,
    { language: "x-default", url: urlContext.globalUrl },
  ];

  // Create JSON-LD schema for web page.
  const webPageSchema: SeoSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: urlContext.canonicalUrl,
    inLanguage: locale.languageCode,
    mainEntityOfPage: urlContext.origin,
    name: title,
    description: description,
    copyrightYear: 2024,
    author: { "@type": "Person", name: "Bared Footwear" },
    creator: { "@type": "Person", name: "Bared Footwear" },
    publisher: { "@type": "Person", name: "Bared Footwear" },
    copyrightHolder: { "@type": "Person", name: "Bared Footwear" },
    potentialAction: {
      "@type": "SearchAction",
      target: "https://baredfootwear.com/search?q={query}",
      query: "required",
    },
  };

  // Create JSON-LD schema for breadcrumbs
  const breadcrumbSchema: SeoSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    description: "Breadcrumbs list",
    name: "Breadcrumbs",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Homepage",
        item: urlContext.origin,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: title,
        item: urlContext.canonicalUrl,
      },
    ],
  };

  return {
    robots,
    jsonLd: [...schemas, webPageSchema, breadcrumbSchema],
    url: urlContext.canonicalUrl,
    ...(media ? { media } : {}),
    ...(title ? { title } : {}),
    ...(description ? { description } : {}),
    ...(alternates ? { alternates } : {}),
    /**
     * Clear the title template if the title already contains the string "Bared Footwear".
     * The title template is set in the root loader.
     * This prevents titles like "Bared Footwear | Bared Footwear".
     */
    ...(title?.includes("Bared Footwear") ? { titleTemplate: "%s" } : {}),
  };
}
